import React, { useCallback, useMemo, useState, isValidElement } from 'react';
import { ConditionalTooltip } from '@atlassian/jira-jql-builder-common/src/ui/conditional-tooltip/index.tsx';
import { ScreenReaderText } from '@atlassian/jira-accessibility/src/common/ui/screenreader-text/index.tsx';
import type { Option, FieldOption, SecondaryLabelComponent } from '../../../common/types.tsx';
import {
	StyledIcon,
	StyledLabel,
	StyledLabelIcon,
	StyledLabelText,
	StyledOption,
	StyledSecondaryLabel,
} from '../../../common/ui/styled.tsx';

type Props = {
	data: Option | FieldOption;
};

export const IconOptionLabel = ({ data }: Props): React.JSX.Element => {
	const [labelOverflows, setLabelOverflows] = useState(false);
	const [secondaryLabelOverflows, setSecondaryLabelOverflows] = useState(false);

	const showSecondaryLabel = (input: Option | FieldOption) =>
		(typeof input.secondaryLabel === 'string' && input.secondaryLabel.length > 0) ||
		// remove !isValidElement(data.secondaryLabel) with cleanup with m7.3-mm2-custom-fields-with-existing-uis_vzhn7
		(typeof input.secondaryLabel === 'object' &&
			!isValidElement(input.secondaryLabel) &&
			isValidElement(input.secondaryLabel.element));

	const onLabelRef = useCallback((node: HTMLElement | null) => {
		if (node != null) {
			setLabelOverflows(node.offsetWidth < node.scrollWidth);
		}
	}, []);

	const onSecondaryLabelRef = useCallback((node: HTMLElement | null) => {
		if (node != null) {
			setSecondaryLabelOverflows(node.offsetWidth < node.scrollWidth);
		}
	}, []);

	const tooltipContent = useMemo(
		() => (
			<>
				<div>{data.label}</div>
				{typeof data.secondaryLabel === 'string' && data.secondaryLabel.length > 0 && (
					// eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx, @atlassian/i18n/no-literal-string-in-jsx
					<div>({data.secondaryLabel})</div>
				)}
			</>
		),
		[data],
	);

	const getSecondaryLabelComponent = (option: string | SecondaryLabelComponent) => {
		if (typeof option === 'object' && !isValidElement(option)) {
			return option.element;
		}
		return <>{option}</>;
	};

	return (
		<ConditionalTooltip
			condition={labelOverflows || secondaryLabelOverflows}
			content={tooltipContent}
			position="right"
		>
			<StyledOption>
				<StyledLabel>
					{data.icon != null && (
						<StyledLabelIcon>
							<StyledIcon src={data.icon} alt={data.label} />
						</StyledLabelIcon>
					)}
					<StyledLabelText ref={onLabelRef}>{data.label}</StyledLabelText>
				</StyledLabel>
				{showSecondaryLabel(data) && (
					<StyledSecondaryLabel
						ref={onSecondaryLabelRef}
						data-testid="jql-builder-basic-picker.ui.format-option-label.icon-option-label.secondary-label"
					>
						{data.secondaryLabel && getSecondaryLabelComponent(data.secondaryLabel)}
					</StyledSecondaryLabel>
				)}
			</StyledOption>
			{data.disabledMessage && <ScreenReaderText>{data.disabledMessage}</ScreenReaderText>}
		</ConditionalTooltip>
	);
};
export default IconOptionLabel;
